import { html } from "lit-html";
import { customElement, property, state } from "lit/decorators.js";
import { ModalScreen } from "./modal-screen";
import gameApp from "@/index";
import { Scene } from "excalibur";

@customElement("navigator-screen")
export class Navigator extends ModalScreen {
  @state() locations: {
    id: number;
    name: string;
    category: string;
    description: string;
    image: string;
  }[] = [];
  @state() activeTab: string = ""; // Active tab should start as empty
  @state() currentPage: number = 1; // current page number
  @state() itemsPerPage: number = 1; // number of items per page
  @state() totalItems: number = 0; // total items count from the server
  @state() totalPages: number = 1; // total pages, based on number of items
  @state() selectedSortField: string = "name"; // default sorting by name
  @state() selectedSortOrder: string = "asc"; // default sorting order

  tabs: { id: string; label: string }[] = [];
  @property() items: any = [];

  @state() searchQuery: string = ""; // Stores the search input
  @state() showSearchForm: boolean = false; // Controls the visibility of the search form

  constructor() {
    super();

    this.tabs = [
      {
        id: "public",
        label: "Public",
      },
      {
        id: "all",
        label: "All locations",
      },
      {
        id: "my_rooms",
        label: "My Locations",
      },
      {
        id: "favorites",
        label: "Favorites",
      },
    ];
  }

  public openModal(): void {
    this.isOpen = true;
    this.fetchLocations("public");
  }

  private async handleFavoriteClick(roomId: number): Promise<void> {
    console.log(`Room ${roomId} added to favorites!`);

    try {
      // Making an API call to the favorite-room endpoint
      const response = await fetch(
        `${process.env.BASE_API_URL}/favorite-room`,
        {
          method: "POST",
          body: JSON.stringify({
            roomId: roomId,
          }),
          credentials: "include", // Include credentials (e.g., cookies) if needed
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (data.message === "UNFAVORITE" || data.message === "FAVORITE") {
          this.items = this.items.map((item) =>
            item.id === roomId
              ? {
                  ...item,
                  isFavorite: data.message === "UNFAVORITE" ? false : true,
                } // Toggle isFavorite based on action
              : item
          );
        }
      } else {
        // If the request fails
        console.error("Failed to favorite room:", response.statusText);
        // Optionally, show an error message to the user
      }
    } catch (error) {
      console.error("Error occurred while favoriting room:", error);
      // Optionally, show a message in the UI about the error
    }
    // Add logic here to manage favorite state or make an API call to save the favorite
  }

  private handleVisitClick(roomId: number): void {
    const scene = gameApp.game.currentScene as Scene;
    scene.clear();

    gameApp.wsClient.sendMessage(
      JSON.stringify({
        action: "leave_room",
      })
    );

    gameApp.wsClient.sendMessage(
      JSON.stringify({
        action: "join_room",
        arguments: {
          roomId,
        },
      })
    );
  }

  public selectTab(tabId: string) {
    this.activeTab = tabId;
    this.currentPage = 1; // Reset page to 1 when tab changes
    this.fetchLocations(tabId);
  }

  // New method to handle sorting changes
  private handleSortChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    const [sortField, sortOrder] = select.value.split("_");
    this.selectedSortField = sortField;
    this.selectedSortOrder = sortOrder;

    this.fetchLocations(this.activeTab);
  }

  // Toggle the search form visibility
  private toggleSearchForm(): void {
    this.showSearchForm = !this.showSearchForm;
  }

  // Handle input change in the search field
  private handleSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchQuery = input.value;
  }

  // Handle the search form submission
  private async handleSearchSubmit(event: Event): Promise<void> {
    event.preventDefault(); // Prevent page reload
    if (this.searchQuery.trim()) {
      this.fetchLocations(this.activeTab, this.searchQuery);
    }
  }

  // Modify fetchLocations to accept a search query, page, and handle pagination
  private async fetchLocations(
    category: string = "public",
    searchQuery: string = "",
    page: number = 1
  ) {
    const limit = this.itemsPerPage;
    const offset = (page - 1) * limit;

    try {
      const url = searchQuery
        ? `${process.env.BASE_API_URL}/rooms?category=${category}&sortField=${this.selectedSortField}&sort=${this.selectedSortOrder}&search=${searchQuery}&limit=${limit}&offset=${offset}`
        : `${process.env.BASE_API_URL}/rooms?category=${category}&sortField=${this.selectedSortField}&sort=${this.selectedSortOrder}&limit=${limit}&offset=${offset}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        this.items = data.rooms;
        this.totalItems = data.pagination.totalItems; // Assuming the response includes total items count

        this.totalPages = Math.ceil(this.totalItems / this.itemsPerPage); // Calculate total pages
        this.currentPage = page; // Update current page
      } else {
        console.error("Failed to fetch data items:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  }

  // Handle page navigation
  private goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.fetchLocations(this.activeTab, this.searchQuery, page);
    }
  }

  render() {
    if (!this.isOpen) return html``;

    return html`
      <div class="modal">
        <div class="modal__top">
          ${this.tabs.map(
            (tab) => html`
              <span
                class="modal__tab ${this.activeTab === tab.id
                  ? "is-active"
                  : ""}"
                @click="${() => this.selectTab(tab.id)}"
              >
                ${tab.label}
              </span>
            `
          )}
          <button class="modal__close" @click="${this.closeModal}">
            <span>Close</span>
          </button>
        </div>

        <div class="modal__body">
          ${this.items.length > 0
            ? html`
                <div class="sort-options">
                  <select
                    id="sortSelect"
                    class="select"
                    @change="${this.handleSortChange}"
                  >
                    <option
                      value="title_asc"
                      ?selected="${this.selectedSortField === "title" &&
                      this.selectedSortOrder === "asc"}"
                    >
                      Order by name (A-Z)
                    </option>
                    <option
                      value="title_desc"
                      ?selected="${this.selectedSortField === "title" &&
                      this.selectedSortOrder === "desc"}"
                    >
                      Order by name (Z-A)
                    </option>
                    <option
                      value="createdAt_asc"
                      ?selected="${this.selectedSortField === "created_at" &&
                      this.selectedSortOrder === "asc"}"
                    >
                      Order by date (Oldest)
                    </option>
                    <option
                      value="createdAt_desc"
                      ?selected="${this.selectedSortField === "created_at" &&
                      this.selectedSortOrder === "desc"}"
                    >
                      Order by date (Newest)
                    </option>
                  </select>

                  <!-- Show Search Button Only When Tab is "All" -->
                  ${this.activeTab === "all" && !this.showSearchForm
                    ? html`
                        <button class="btn" @click="${this.toggleSearchForm}">
                          Search Locations
                        </button>
                      `
                    : ""}

                  <!-- Search Form -->
                  ${this.showSearchForm && this.activeTab === "all"
                    ? html`
                        <div class="search-form">
                          <input
                            type="text"
                            class="input"
                            .value="${this.searchQuery}"
                            @input="${this.handleSearchInput}"
                            placeholder="Search locations..."
                          />
                          <button
                            class="btn"
                            @click="${this.handleSearchSubmit}"
                          >
                            Submit
                          </button>
                        </div>
                      `
                    : ""}
                </div>
              `
            : ""}

          <div class="modal__results">
            ${this.items.length === 0
              ? html`<div class="message">
                  You have not created any locations yet.
                </div>`
              : html`
                  ${this.items.map(
                    (item) => html`
                      <div
                        class="navigator_location"
                        style="align-items: center"
                      >
                        <div class="navigator_location__preview">
                          <div class="navigator_location__title">
                            ${item.name}
                          </div>
                          <div class="navigator_location__user">
                            ${item.ownerName}
                          </div>
                        </div>
                        <div>
                          <img
                            src="public/gui/lock.png"
                            style="height: auto; position: relative;"
                            alt="Coin"
                          />
                        </div>
                        <div>
                          <button
                            class="btn"
                            @click="${() => this.handleFavoriteClick(item.id)}"
                          >
                            ${item.isFavorite ? "Unfavorite" : "Favorite"}
                          </button>
                          <button
                            class="btn"
                            @click="${() => this.handleVisitClick(item.id)}"
                          >
                            Visit
                          </button>
                        </div>
                      </div>
                    `
                  )}
                `}
          </div>

          ${this.items.length > 0
            ? html`
                <div class="pagination">
                  <button
                    class="btn is-prev ${this.currentPage === 1
                      ? "is-disabled"
                      : ""}"
                    @click="${() => this.goToPage(this.currentPage - 1)}"
                    ?disabled="${this.currentPage === 1}"
                  >
                    &#8249;
                  </button>
                  ${Array.from(
                    { length: this.totalPages },
                    (_, index) => index + 1
                  ).map(
                    (page) => html`
                      <button
                        @click="${() => this.goToPage(page)}"
                        ?disabled="${page === this.currentPage}"
                        class="btn ${page === this.currentPage
                          ? "is-active"
                          : ""}"
                      >
                        ${page}
                      </button>
                    `
                  )}
                  <button
                    class="btn is-next ${this.currentPage ===
                    Math.ceil(this.totalItems / this.itemsPerPage)
                      ? "is-disabled"
                      : ""}"
                    @click="${() => this.goToPage(this.currentPage + 1)}"
                    ?disabled="${this.currentPage === this.totalPages}"
                  >
                    &#8250;
                  </button>
                </div>
              `
            : ""}
        </div>
      </div>
    `;
  }
}
